/* Craft memory - what this browser has shown an interest in.
 *
 * Stored, in this browser only: craft NAMES, size letters, a count, a
 * timestamp. Never: name, email, phone, address, customer id, order id,
 * product id, title or price. The record cannot identify anyone - it says
 * "this browser likes Ajrakh and picks M", not who is holding it. Nothing is
 * sent to Sujatra's servers; there is no network call in this file at all.
 *
 * WHY VIEWS AND PURCHASES ARE KEPT APART
 *
 * They are worth wildly different amounts. Looking at a Kantha kurta is idle
 * curiosity; having bought one is the single best predictor we measured - a
 * Benarasi buyer is 5.2x likelier than chance to buy Benarasi again. So they
 * live in separate buckets and BOUGHT_WEIGHT decides the exchange rate. Mixed
 * into one counter, ten minutes of browsing would outrank a real purchase.
 *
 * DECAY
 *
 * Interest is not permanent. A score halves every HALF_LIFE_DAYS, so last
 * spring's browsing fades rather than defining someone forever. Decay is
 * applied at READ time, not by a timer, so a browser closed for a month comes
 * back correctly faded with no background work.
 *
 * IT MUST SURVIVE FAILING
 *
 * localStorage throws outright in Safari private mode and in some embedded
 * browsers, and Safari's tracking prevention clears script-written storage
 * after about seven days without a visit. So EVERY read and write is wrapped,
 * and an empty memory is a normal state, not an error.
 */
(function () {
  'use strict';

  var KEY = 'sjt_craft_memory_v1';
  var MAX_CRAFTS = 16;          // evict the weakest beyond this
  var HALF_LIFE_DAYS = 30;      // a score halves every 30 days
  var BOUGHT_WEIGHT = 5;        // one purchase is worth five views
  var DAY = 86400000;

  /* Shopify's Customer Privacy API is only present once the consent banner
   * loads, and only on stores that use one. When it is there we respect it;
   * when it is absent there is no banner to contradict, so we proceed. Reading
   * is always allowed - declining consent should stop us LEARNING more, not
   * make us forget what is already on the device.
   */
  function mayWrite() {
    try {
      var p = window.Shopify && window.Shopify.customerPrivacy;
      if (!p) return true;
      if (typeof p.preferencesProcessingAllowed === 'function') return p.preferencesProcessingAllowed();
      if (typeof p.analyticsProcessingAllowed === 'function') return p.analyticsProcessingAllowed();
      return true;
    } catch (e) { return true; }
  }

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return { v: 1, views: {}, bought: {}, sizes: {} };
      var o = JSON.parse(raw);
      if (!o || o.v !== 1) return { v: 1, views: {}, bought: {}, sizes: {} };
      o.views = o.views || {};
      o.bought = o.bought || {};
      o.sizes = o.sizes || {};
      return o;
    } catch (e) { return { v: 1, views: {}, bought: {}, sizes: {} }; }
  }

  function write(rec) {
    try {
      rec.updated = Date.now();
      window.localStorage.setItem(KEY, JSON.stringify(rec));
      return true;
    } catch (e) { return false; }   // private mode, quota, disabled storage
  }

  /** A count faded by how long ago it was last touched. */
  function decayed(entry, now) {
    if (!entry || !entry.n) return 0;
    var age = (now - (entry.last || now)) / DAY;
    if (age <= 0) return entry.n;
    return entry.n * Math.pow(0.5, age / HALF_LIFE_DAYS);
  }

  function bump(bucket, crafts, now) {
    for (var i = 0; i < crafts.length; i++) {
      var c = String(crafts[i] || '').trim();
      if (!c) continue;
      var e = bucket[c];
      // Decay what is already there BEFORE adding, so a craft looked at five
      // times last year does not sit at 5 forever waiting to be re-counted.
      bucket[c] = { n: (e ? decayed(e, now) : 0) + 1, last: now };
    }
  }

  /** Keep the strongest MAX_CRAFTS; a runaway record helps nobody. */
  function trim(bucket, now) {
    var keys = Object.keys(bucket);
    if (keys.length <= MAX_CRAFTS) return bucket;
    keys.sort(function (a, b) { return decayed(bucket[b], now) - decayed(bucket[a], now); });
    var out = {};
    for (var i = 0; i < MAX_CRAFTS; i++) out[keys[i]] = bucket[keys[i]];
    return out;
  }

  /** Record crafts from a product page view. */
  function recordView(crafts) {
    if (!crafts || !crafts.length || !mayWrite()) return;
    var now = Date.now();
    var rec = read();
    bump(rec.views, crafts, now);
    rec.views = trim(rec.views, now);
    write(rec);
  }

  /**
   * Record crafts the person has actually bought.
   *
   * Idempotent by design: this runs on every page load where Liquid can see
   * the customer's orders, so it SETS the count rather than incrementing it.
   * Incrementing would inflate a purchase by one for every page they visit.
   */
  function recordPurchases(counts) {
    if (!counts || !mayWrite()) return;
    var names = Object.keys(counts);
    if (!names.length) return;
    var now = Date.now();
    var rec = read();
    for (var i = 0; i < names.length; i++) {
      var n = Number(counts[names[i]]) || 0;
      if (n > 0) rec.bought[names[i]] = { n: n, last: now };
    }
    rec.bought = trim(rec.bought, now);
    write(rec);
  }

  /** Ranked crafts, strongest first. Purchases outweigh views. */
  function top(limit) {
    var now = Date.now();
    var rec = read();
    var scores = {};
    var k;
    for (k in rec.views) if (rec.views.hasOwnProperty(k)) {
      scores[k] = (scores[k] || 0) + decayed(rec.views[k], now);
    }
    for (k in rec.bought) if (rec.bought.hasOwnProperty(k)) {
      scores[k] = (scores[k] || 0) + decayed(rec.bought[k], now) * BOUGHT_WEIGHT;
    }
    var out = Object.keys(scores).map(function (craft) {
      return {
        craft: craft,
        score: Math.round(scores[craft] * 100) / 100,
        bought: !!rec.bought[craft]
      };
    });
    out.sort(function (a, b) { return b.score - a.score; });
    return typeof limit === 'number' ? out.slice(0, limit) : out;
  }

  /* SIZE - NOT A STOCK CLAIM.
   *
   * Every sized garment here carries inventoryPolicy CONTINUE, so "available
   * in your size" is a sentence the data cannot support: a variant at minus
   * 156 units still reads as buyable. "Your usual size" is a fact about the
   * PERSON, not about the shelf - safer, and the more personal thing to say.
   *
   * Captured from the variant picker, so it works for a shopper who has never
   * signed in. A size actually ORDERED is merged in by Liquid and weighted,
   * because ordering an M outranks idly clicking one.
   */
  function recordSize(size, weight) {
    var v = String(size || '').trim();
    if (!v || v.length > 20 || !mayWrite()) return;
    var rec = read();
    rec.sizes[v] = (rec.sizes[v] || 0) + (Number(weight) || 1);
    write(rec);
  }

  /** The size they pick most. null when there is no clear answer. */
  function usualSize() {
    var sizes = read().sizes || {};
    var best = null, bestN = 0;
    for (var k in sizes) if (sizes.hasOwnProperty(k)) {
      if (sizes[k] > bestN) { bestN = sizes[k]; best = k; }
    }
    return bestN >= 2 ? best : null;   // one click is a click, not a size
  }

  /** A size token, not "Semi Stitched" or a colour that happens to sit nearby. */
  var SIZE_LIKE = /^(XXS|XS|S|M|L|XL|XXL|XXXL|2XL|3XL|4XL)$/i;

  function watchVariantPicker() {
    try {
      document.addEventListener('change', function (e) {
        var el = e.target;
        if (!el || !el.value) return;
        var value = String(el.value).trim();
        if (!SIZE_LIKE.test(value)) return;
        recordSize(value, 1);
      }, true);
    } catch (e) { /* never break the page */ }
  }

  function forget() {
    try { window.localStorage.removeItem(KEY); return true; } catch (e) { return false; }
  }

  window.SujatraCraftMemory = {
    recordView: recordView,
    recordPurchases: recordPurchases,
    recordSize: recordSize,
    usualSize: usualSize,
    top: top,
    forget: forget,
    raw: read,
    mayWrite: mayWrite,
    _config: { KEY: KEY, MAX_CRAFTS: MAX_CRAFTS, HALF_LIFE_DAYS: HALF_LIFE_DAYS, BOUGHT_WEIGHT: BOUGHT_WEIGHT }
  };

  /* The readout. Only with ?craftmemory=1 in the URL. This exists so a person
   * can browse the shop and watch the memory form, which is the whole point of
   * shipping the signal before the rail: if the panel never shows anything
   * convincing, there is nothing worth building on top.
   */
  function panel() {
    try {
      if (window.location.search.indexOf('craftmemory=1') === -1) return;
      var el = document.createElement('div');
      el.id = 'sjt-craft-memory-panel';
      el.setAttribute('style', [
        'position:fixed', 'right:12px', 'bottom:12px', 'z-index:2147483000',
        'max-width:290px', 'max-height:60vh', 'overflow:auto',
        'background:#fff', 'color:#1a1410', 'border:1px solid #e8e2d9',
        'border-radius:10px', 'padding:12px 14px',
        'box-shadow:0 6px 24px rgba(0,0,0,.12)',
        'font:12px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace'
      ].join(';'));

      function paint() {
        var rows = top();
        var stored = window.SujatraCraftMemory.mayWrite();
        var html = '<div style="font-weight:600;letter-spacing:.08em;text-transform:uppercase;font-size:10px;color:#b0a090;margin-bottom:8px">Craft memory</div>';
        if (!rows.length) {
          html += '<div style="color:#8a7f75">Nothing yet - open a few product pages.</div>';
        } else {
          var max = rows[0].score || 1;
          html += rows.map(function (r) {
            var w = Math.max(3, Math.round((r.score / max) * 100));
            return '<div style="margin:5px 0">'
              + '<div style="display:flex;justify-content:space-between;gap:8px">'
              + '<span>' + (r.bought ? '&#9679; ' : '') + r.craft + '</span>'
              + '<span style="color:#b0a090">' + r.score + '</span></div>'
              + '<div style="height:3px;background:#f0ece7;border-radius:2px;margin-top:2px">'
              + '<div style="height:3px;width:' + w + '%;background:#c9a96e;border-radius:2px"></div></div>'
              + '</div>';
          }).join('');
          html += '<div style="margin-top:8px;color:#b0a090;font-size:10px">&#9679; = bought, weighted &times;' + BOUGHT_WEIGHT + '</div>';
        }
        var sz = usualSize();
        html += '<div style="margin-top:6px;color:#b0a090;font-size:10px">usual size: ' + (sz || '-') + '</div>';
        if (!stored) html += '<div style="margin-top:8px;color:#a32d2d;font-size:10px">Consent declined - not learning.</div>';
        html += '<div style="margin-top:10px"><button type="button" id="sjt-cm-forget" style="font:inherit;cursor:pointer;background:#f7f4ef;border:1px solid #e8e2d9;border-radius:6px;padding:4px 8px">Forget</button></div>';
        el.innerHTML = html;
        var b = el.querySelector('#sjt-cm-forget');
        if (b) b.addEventListener('click', function () { forget(); paint(); });
      }

      paint();
      document.body.appendChild(el);
    } catch (e) { /* the readout must never break the page */ }
  }

  function boot() {
    try {
      var node = document.getElementById('craft-memory-data');
      if (node) {
        var data = JSON.parse(node.textContent || '{}');
        if (data.pageCrafts && data.pageCrafts.length) recordView(data.pageCrafts);
        if (data.purchased) recordPurchases(data.purchased);
        // A size on an order outranks a size on a click, so it arrives weighted.
        if (data.orderedSizes) {
          for (var sz in data.orderedSizes) {
            if (data.orderedSizes.hasOwnProperty(sz)) recordSize(sz, 3 * data.orderedSizes[sz]);
          }
        }
      }
    } catch (e) { /* a malformed payload must not break the page */ }
    watchVariantPicker();
    panel();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
